import getMqttBrokerHostname from './getMqttBrokerHostname';
import globalConfig from './globalConfig';
import * as auth0 from './auth0';

/**
 * EMQX's TLS listener. `MqttClient.java` defaults a bare `host:port` to `ssl://` and only allows
 * plaintext in debug builds, which matters because CONNECT's password carries an Auth0 bearer token
 * (see `setCredentials`).
 */
const EMQX_TLS_PORT = 8883;

/** Seam for tests: the two things this module reaches for live here. */
const _p = {
    getMqttCredentials: options => auth0.getMqttCredentials(options),
    getGlobalConfig: () => globalConfig()
};

/**
 * Everything a Continuous Scan session needs to reach the broker as the right tenant, derived once.
 *
 * **Why this is a module rather than four lines in each component.** Three components start a scan —
 * `MarkRecord/index.js`, `MultiPickerTransactionGrid/index.js` and its `.native.js` twin — and each
 * used to derive this block itself. That is the seam the tenant-ID and broker-hostname bugs both came
 * through, because Metro resolves `.native.js` on the device while Node (and therefore every test in
 * this repo) resolves `index.js`: a fix and its new tests can both land on the variant that never runs
 * Continuous Scan in production, and the suite stays green while the device keeps the old behaviour.
 * That happened in #1715, in #1723, and again with the tenant-ID normalization. One function, called
 * by all three, is what removes the opportunity rather than catching it in review a fourth time.
 *
 * **The tenant ID is the part that must not be re-derived by the caller.** `createMqttCredentials`
 * lower-cases it into the `cs_<tenantId>` username; silo/security slices the tenant back out of that
 * username and requires it to equal the `<tenant-id>` segment of `stream/update/<tenant-id>/...`,
 * which `ContinuousScanSession.java` builds from `sessionConfig.tenantId`. Two sources for one value
 * let those disagree on case, and an upper-case tenant then authenticates and is denied every publish
 * and subscribe — #1715's symptom by another route. So the normalized id is returned here, in the same
 * object as the username derived from it, and callers spread the result rather than assembling it.
 *
 * @param {{ tenantId: string }} options the tenant the operator selected, as the component holds it
 * @returns {Promise<{ tenantId: string, group: string|null, mqttBrokerUrl: string,
 *   mqttUsername: string, mqttPassword: string }>} spread straight onto `sessionConfig`
 */
export async function getContinuousScanTransport({ tenantId }) {
    const config = _p.getGlobalConfig();

    // Per CS-002 (docs/product/decisions/continuous-scan/mqtt-authentication.md): the security silo
    // authenticates with `cs_<tenantId>` as the username and the user's current Auth0 access token as
    // the password. The `cs_` prefix is a reserved namespace; a bare tenant ID collides with
    // provisioned reader titles.
    const credentials = await _p.getMqttCredentials({ tenantId });

    return {
        // The normalized id, never the argument — see above.
        tenantId: credentials.tenantId,
        // The same tenant deployment-group number that routes HTTP (`getFullURL`'s `/g/N`) and the
        // socket connection. MQTT topics need the equivalent `gN/` prefix so EMQX's authorization
        // webhook resolves them against the right group (see `stripGroupPrefix` in silo_security's
        // helpers/mqtt.js). Null rather than absent when there is no group, so the native side reads
        // one shape either way.
        group: config.groupNumber ? String(config.groupNumber) : null,
        // EMQX is deployed as its own service, never on the app's own hostname, so it needs the same
        // rewrite used for rules-engine forward actions (willChange_rulesEngine_customerWorkflow.js).
        mqttBrokerUrl: `${getMqttBrokerHostname(config.hostname)}:${EMQX_TLS_PORT}`,
        mqttUsername: credentials.username,
        mqttPassword: credentials.password
    };
}

export const _private = _p;
